import { useInfiniteQuery } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { useAuth } from "~/features/auth/useAuth"
import { GetEntitlementsDocument, type GetEntitlementsQuery } from "~/graphql/generated/graphql"
import { useGraphQLClient } from "~/shared/api/GraphQLClientContext"
import { toEntitlementItem, withSeatOrdinals } from "../projections"
import { logEntitlementSeats } from "../seatTelemetry"
import type { EntitlementItem } from "../types"

export interface EntitlementsResult {
  readonly items: readonly EntitlementItem[]
  /** True until every page is in, not just the first — see `MAX_PAGES`. */
  readonly isLoading: boolean
  readonly isError: boolean
  /** The cap was hit and rows remain unfetched. The one case the page must not render as complete. */
  readonly isTruncated: boolean
  readonly fetchNextPage: () => void
  readonly isFetchingNextPage: boolean
}

/**
 * A safety stop, not a page size: 500 seats is far past any real account (the UAT
 * one has 68) and exists only so a facade bug that never clears `hasNextPage`
 * cannot spin forever. Hitting it is a defect, and the page says so rather than
 * pretending the list is complete.
 */
const MAX_PAGES = 10

const nextCursor = (page: GetEntitlementsQuery): string | undefined => {
  const pageInfo = page.user?.entitlements?.pageInfo

  return pageInfo?.hasNextPage ? (pageInfo.endCursor ?? undefined) : undefined
}

/**
 * POC only. These fields exist on the local facade, never on the deployed graph,
 * so the query is disabled unless VITE_POC_FACADE is set — otherwise every load
 * of the page would fire a request the real graph rejects as an unknown field.
 *
 * Sign-in is not required for the rows to arrive: the facade authenticates to
 * commerce with its own env token. That is the POC's central fiction — the rows
 * belong to whoever minted MLC_ACCESS_TOKEN, not to the signed-in user. See
 * POC.md.
 */
/**
 * Every page, fetched without being asked.
 *
 * The page buckets these rows into eight sections client-side and matches them
 * against `libraryV2` cards by title, and BOTH of those are only correct over the
 * complete set. On the UAT account the three simulcast seats are rows 66-68 of
 * 68, so a single `first: 50` page contained none of them: the expired events
 * were absent from Digital Events, the `Until 11/8/26` pill was missing from the
 * live one, and every section that depends on the bucketing quietly disagreed
 * with myLifeway — all of it indistinguishable from an account that owns no
 * events. A "Load more" button at the bottom of one section is not a fix for
 * that; it is the bug wearing a control.
 *
 * Cheap in practice: 68 rows is two round trips, and `/seats` is one call behind
 * the facade either way. Production would ask for the whole list too — MLC's own
 * page does, which is why its Events table has always shown the expired rows.
 */
export const useEntitlements = (): EntitlementsResult => {
  const client = useGraphQLClient()
  const { status } = useAuth()
  const enabled = Boolean(import.meta.env.VITE_POC_FACADE) && status !== "initializing"

  const query = useInfiniteQuery({
    queryKey: ["entitlements"],
    queryFn: ({ pageParam }) => client.request(GetEntitlementsDocument, { cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    enabled,
  })

  const pageCount = query.data?.pages.length ?? 0
  const isCapped = pageCount >= MAX_PAGES

  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage && !isCapped) {
      query.fetchNextPage()
    }
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage, isCapped])

  /**
   * Loading until the list is whole. A section that renders its first page and
   * then grows would show a customer an empty `Digital Events` before filling it
   * in, which reads as a bug and is worse than a moment more of skeleton.
   */
  const isDraining = Boolean(query.hasNextPage) && !isCapped

  /** Memoized so the telemetry effect below fires when the data changes, not on every render. */
  const nodes = useMemo(
    () => (query.data?.pages ?? []).flatMap(page => page.user?.entitlements?.edges ?? []).map(edge => edge.node),
    [query.data?.pages],
  )
  const items = useMemo(() => withSeatOrdinals(nodes.map(toEntitlementItem)), [nodes])

  /**
   * Once the list is whole, not on every page: the sections and the title match
   * are only correct over the complete set, so a per-page dump would show three
   * different answers to the same question.
   */
  useEffect(() => {
    if (!isDraining) {
      logEntitlementSeats(nodes, items)
    }
  }, [isDraining, nodes, items])

  return {
    items,
    isLoading: enabled && (query.isLoading || isDraining),
    isError: query.isError,
    isTruncated: Boolean(query.hasNextPage) && isCapped,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      query.fetchNextPage()
    },
  }
}
